/**
 * 教学描述（/me/moves/new，TEACH-PAGES-117）：告诉我们 TA 是怎么做的，先存成一份草稿（adm1：不占次数，次数用完也能存）。
 * - 一个输入区、一句看得见的示例（不放进 placeholder：占位字对比度不够）、一个主要按钮；存好以后到这一单的页面，焦点落在那一页的标题上。
 * - 视觉照 r7k 参考稿 03-describe-A（TEACH-PAGES-VISUAL-117，I 10-16）：一张木桌，TA（小窝同一个 PetFigure 整身图）从桌子后面探头，
 *   输入区是摊在桌上的一本线圈笔记本。主人上传的参考像拍立得贴着、「整理一下」都等上传口和整理那一步（TEACH-WIRE-117）开放再放，
 *   这一版不放、不许诺；参考稿里提照片、视频的引导语也不照抄。TA 探头只是装饰，读屏不念。
 * - 次数用完时（服务端 exhausted）：标题、引导、按钮都说「先记下想法」，说清收费开放以后才能接着教（设计评审第一轮高 5）。
 * - 参考照片、视频：上传口由 adm1 后续小包挂（P 10-16：每单照片 ≤3、视频在设备上抽帧 ≤6），挂上以前不放上传、也不在引导里许诺（评审高 4）。
 * - 输入框用全站的 .ps-textarea（3:1 边线、16px 字号，iOS 聚焦不放大；评审阻断 2、高 8、高 9）。
 * - 存草稿要幂等键：同一段字重发用同一个键（不会建出第二单）；改了字再存才换键。
 * 同一个表单也给草稿页续写用（DescribeForm），续写走改草稿（PUT，不要键）；没改动时「改好了」不可按（aria-disabled，不真禁用：焦点不丢，评审第二轮高 1）。
 * 键不能按时，键上面一行说为什么（空的：先写几句；没改动：还没有改动），并接到键的说明上（6c2b 轻审挑刺）。
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import { LoadingState, Page, TopBar } from "@/shared/ui";
import { PetFigure, usePetCharacter } from "@/features/home/PetFigure";
import { WorldGate } from "@/features/world_map/WorldGate";
import { useCurrentPet, type CurrentPet } from "@/features/memories/currentPet";
import { DESCRIBE_EXAMPLE, DESCRIBE_LABEL, DESCRIBE_MAX, DESCRIBE_SAVE, DESCRIBE_SAVE_IDEA, DESCRIBE_WHY_EMPTY, DESCRIBE_WHY_SAME, SAVE_IDEA_LEAD, TEACH_ACTION, TEACH_ENTRY_TITLE, TEACH_INVITE, TEACH_SAVE_IDEA, canTeachNew } from "./teachModel";
import type { PetPrivateSummary } from "@/shared/contracts";
import type { TeachOrder } from "./teachTypes";
import { teachKeys, useCanCare, useEntitlement, useTeachApi, useTeachOpen } from "./useTeach";
import { TEACH_CLOSED, TEACH_DEMO, TEACH_NOT_CARER, TEACH_NO_PET } from "./TeachHomePage";
import { TeachButton } from "./TeachButton";
import "@/features/me/me.css";
import "./teach.css";

export function TeachDescribePage() {
  return (
    <WorldGate>
      <DescribeBody />
    </WorldGate>
  );
}

function DescribeBody() {
  const api = useTeachApi();
  const open = useTeachOpen();
  const canCare = useCanCare();
  const current = useCurrentPet();
  const entitlement = useEntitlement();
  const idea = entitlement.data ? !canTeachNew(entitlement.data) : false;
  const body = !api ? <p className="ps-teach-quiet" role="status">{TEACH_DEMO}</p>
    : current.status !== "ready" || open === null || canCare === null || entitlement.isPending ? <LoadingState lines={2} label="正在找到 TA…" />
    : !current.pet ? <p className="ps-teach-quiet" role="status">{TEACH_NO_PET}</p>
    : !open ? <p className="ps-teach-quiet" role="status">{TEACH_CLOSED}</p>
    : !canCare ? <p className="ps-teach-quiet" role="status">{TEACH_NOT_CARER}</p>
    : <NewDraft pet={current.pet} idea={idea} />;
  return (
    <Page bare className="ps-teach">
      {/* 额度还没读到时标题先写中性的，不先闪「教 TA 一个新动作」再换（评审第二轮挑刺） */}
      <TopBar title={!entitlement.data && entitlement.isPending ? TEACH_ENTRY_TITLE : idea ? TEACH_SAVE_IDEA : TEACH_ACTION} back="/me/moves" backLabel="返回 TA 的小动作" />
      {body}
    </Page>
  );
}

function NewDraft({ pet, idea }: { pet: CurrentPet; idea: boolean }) {
  const api = useTeachApi()!;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userId = useOptionalCurrentHousehold()?.userId ?? "-";
  const keyFor = useRef<{ text: string; key: string } | null>(null);
  const save = useMutation({
    mutationFn: (text: string) => {
      if (!keyFor.current || keyFor.current.text !== text) keyFor.current = { text, key: newIdempotencyKey("teach-draft") };
      return api.createDraft(pet.petId, { draft_text: text, refs: [] }, keyFor.current.key);
    },
    onSuccess: (order: TeachOrder) => {
      queryClient.setQueryData(teachKeys.order(userId, order.order_id), order);
      void queryClient.invalidateQueries({ queryKey: teachKeys.orders(userId, pet.petId) });
      navigate(`/me/moves/${encodeURIComponent(order.order_id)}`, { replace: true, state: { teachFocus: "title" } });
    },
  });
  return (
    <section className="ps-teach-desk">
      <p className="ps-teach-desk__lead">{idea ? SAVE_IDEA_LEAD : TEACH_INVITE}</p>
      <DeskPeek pet={pet} />
      <div className="ps-teach-desk__top">
        <DescribeForm initial="" busy={save.isPending} error={save.error} submitLabel={idea ? DESCRIBE_SAVE_IDEA : DESCRIBE_SAVE} onSubmit={(text) => save.mutate(text)} />
      </div>
    </section>
  );
}

/** TA 从桌子后面探头：小窝同一个 PetFigure（透明整身图），下半截让桌沿遮住；没有整身图时 PetFigure 自己退成头像，整个露出来 */
export function DeskPeek({ pet }: { pet: Pick<CurrentPet, "petId" | "name"> }) {
  const household = useOptionalCurrentHousehold();
  const entry = household?.pets.find(({ pet: p }) => p.pet_id === pet.petId) ?? null;
  const character = usePetCharacter(pet.petId);
  if (!entry) return <div className="ps-teach-desk__peek" aria-hidden="true" />;
  const summary: PetPrivateSummary = {
    pet_id: entry.pet.pet_id, home_id: entry.household.home_id ?? null, name: entry.pet.name, species: entry.pet.species, photo_url: entry.pet.photo_url,
    origin: entry.pet.origin, owner_title: null, presence: entry.pet.presence, photo_version: entry.pet.photo_version ?? null,
  };
  return (
    <div className="ps-teach-desk__peek" aria-hidden="true">
      <div className="ps-teach-desk__body">
        <PetFigure pet={summary} state={character} />
      </div>
    </div>
  );
}

/**
 * 描述表单：新建和草稿续写共用。只管字，不管存到哪；没改动（和 initial 一样）时不可点。
 * 草稿页（TEACH-UNDERSTAND-UI-117）：有没有没存的改动报给外面（整理的是存上的那一版）；描述在外面被换掉了
 * （整理以后挑一个先教、照简单一点的改），而这里原来没有没存的改动，就跟着换成新的字；有没存的改动就不动主人正在写的字。
 */
export function DescribeForm({ initial, busy, error, submitLabel, onSubmit, onDirtyChange }: { initial: string; busy: boolean; error: unknown; submitLabel: string; onSubmit: (text: string) => void; onDirtyChange?: (dirty: boolean) => void }) {
  const [text, setText] = useState(initial);
  const trimmed = text.trim();
  const unchanged = trimmed === initial.trim();
  const last = useRef(initial);
  useEffect(() => {
    if (last.current === initial) return;
    if (text.trim() === last.current.trim()) setText(initial);
    last.current = initial;
  }, [initial, text]);
  useEffect(() => {
    onDirtyChange?.(!unchanged);
  }, [unchanged, onDirtyChange]);
  const message = error ? toApiError(error).playerMessage : null;
  const why = busy ? null : !trimmed ? DESCRIBE_WHY_EMPTY : unchanged ? DESCRIBE_WHY_SAME : null;
  return (
    <form
      className="ps-teach-card ps-teach-form ps-teach-notebook"
      onSubmit={(event) => {
        event.preventDefault();
        if (trimmed && !unchanged && !busy) onSubmit(trimmed);
      }}
    >
      <label className="ps-teach-form__label" htmlFor="teach-describe">{DESCRIBE_LABEL}</label>
      <p className="ps-teach-note ps-teach-form__hint" id="teach-describe-example">{DESCRIBE_EXAMPLE}</p>
      <textarea
        id="teach-describe"
        className="ps-textarea"
        value={text}
        maxLength={DESCRIBE_MAX}
        rows={5}
        aria-describedby="teach-describe-example teach-describe-count"
        onChange={(event) => setText(event.target.value)}
      />
      <p className="ps-teach-form__count" id="teach-describe-count">{text.length}/{DESCRIBE_MAX}</p>
      {message ? <p className="ps-teach-form__error" role="alert">{message}</p> : null}
      {why ? <p className="ps-teach-form__why" id="teach-describe-why">{why}</p> : null}
      <TeachButton type="submit" variant="primary" block busy={busy} unavailable={!trimmed || unchanged} describedBy={why ? "teach-describe-why" : undefined}>{submitLabel}</TeachButton>
    </form>
  );
}
